import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/dateFormat';
import io from 'socket.io-client';
import { motion, AnimatePresence } from 'framer-motion';
import EventChatModal from './EventChatModal';
import NotesModal from './NotesModal';
import ConfirmModal from './ConfirmModal';
import { useVirtualScroll, useMemoryCleanup, useComponentLifecycle } from '../hooks/usePerformanceOptimization';

// Wir importieren ChangeVibeModal hier nicht mehr, es wird von der MapPage gesteuert.

const ICONS = { myGonkles: '🎮', joined: '🕹️', pending: '❓' };

const MyEventsModal = ({ onClose, onEventClick, onOpenChangeVibe }) => {
    const { user, api } = useAuth();
    const { t, i18n } = useTranslation();
    const { deleteEvent, leaveEvent, updateParticipantStatus } = useEvents();
    const [activeTab, setActiveTab] = useState('myGonkles');
    const [myVibes, setMyVibes] = useState([]);
    const [joinedVibes, setJoinedVibes] = useState([]);
    const [pendingVibes, setPendingVibes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showChat, setShowChat] = useState(false);
    const [selectedChatEvent, setSelectedChatEvent] = useState(null);
    const [showNotes, setShowNotes] = useState(false);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        type: 'danger',
        title: '',
        message: '',
        onConfirm: null
    });
    const socketRef = useRef(null);

    // Performance Optimization Hooks
    const { addTimer, addInterval, addListener } = useMemoryCleanup();
    useComponentLifecycle('MyEventsModal');

    // Virtual Scrolling für große Listen
    const getActiveEvents = useCallback(() => {
        switch (activeTab) {
            case 'myGonkles': return myVibes;
            case 'joined': return joinedVibes;
            case 'pending': return pendingVibes;
            default: return [];
        }
    }, [activeTab, myVibes, joinedVibes, pendingVibes]);

    const activeEvents = getActiveEvents();
    const {
        visibleItems,
        totalHeight,
        containerRef,
        handleScroll,
        containerStyle,
        contentStyle
    } = useVirtualScroll(activeEvents, 80, 400); // 80px pro Event, 400px Container Höhe

    const fetchMyPersonalEvents = useCallback(async () => {
        if (!api || !user) return;
        setIsLoading(true);
        try {
            const res = await api.get('/api/events/my-events');
            if (res.data) {
                setMyVibes(res.data.filter(e => e.user_id === user.id));
                setJoinedVibes(res.data.filter(e => e.user_id !== user.id && e.participants.some(p => p.user_id === user.id && p.status === 'accepted')));
                setPendingVibes(res.data.filter(e => e.user_id !== user.id && e.participants.some(p => p.user_id === user.id && p.status === 'pending')));
            }
        } catch (e) { console.error("Could not fetch personal events", e); }
        finally { setIsLoading(false); }
    }, [api, user]);

    useEffect(() => {
        fetchMyPersonalEvents();
        // SOCKET.IO ENTFERNT - Nur EventContext nutzt Socket
        // MyEventsModal lädt Events über normale API
        return () => {
            // Kein Socket zum Aufräumen
        };
    }, [api, fetchMyPersonalEvents]);

    const handleDeleteWrapper = async (eventId) => {
        setConfirmModal({
            isOpen: true,
            type: 'danger',
            title: t('delete_gonkle_title', 'Gonkle löschen'),
            message: t('delete_gonkle_confirm', 'Bist du sicher, dass du diesen Gonkle löschen möchtest? Das kann nicht rückgängig gemacht werden.'),
            onConfirm: async () => {
                try {
                    await deleteEvent(eventId);
                    fetchMyPersonalEvents(); // Events neu laden
                } catch (error) {
                    console.error('Failed to delete event:', error);
                    alert(t('delete_failed', 'Gonkle konnte nicht gelöscht werden. Möglicherweise hast du keine Berechtigung.'));
                }
            }
        });
    };
    const handleLeaveWrapper = (eventId) => {
        setConfirmModal({
            isOpen: true,
            type: 'warning',
            title: t('leave_gonkle_title', 'Gonkle verlassen'),
            message: t('leave_gonkle_confirm', 'Bist du sicher, dass du diesen Gonkle verlassen möchtest?'),
            onConfirm: () => {
                leaveEvent(eventId);
            }
        });
    };
    const handleParticipantStatusWrapper = (eventId, userId, status) => {
        updateParticipantStatus(eventId, userId, status);
    };
    
    if (!user) return null;

    return (
        <>
        <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 1100 }}>
            <motion.div
                className="modal-content floating-modal"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 150, damping: 20 }}
                onClick={e => e.stopPropagation()}
                style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', height: '80vh' }}
            >
                <div style={{flexShrink: 0}}>
                    <div style={{display:'flex', alignItems:'center', gap: '20px', padding: '20px', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)', borderBottom: '1px solid rgba(99, 102, 241, 0.1)'}}>
                        <img src={user.selfie_path ? `/${user.selfie_path}`: ''} alt="me" style={{width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: `2px solid #cbd5e1`}} />
                        <div style={{flex: 1}}>
                            <h2 style={{margin:0, fontFamily: 'var(--font-display)', fontSize: '2em', color: '#1a202c', fontWeight: '700'}}>{user.nickname}</h2>
                            <div style={{display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap'}}>
                                <button onClick={onOpenChangeVibe} className="button-secondary button-small">
                                    {t('change_favorite_gonkle_button')}
                                </button>
                                <button onClick={() => setShowNotes(true)} className="button-secondary button-small">
                                    📝
                                </button>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', borderBottom: '2px solid rgba(99, 102, 241, 0.2)', borderTop: '2px solid rgba(99, 102, 241, 0.2)', background: '#ffffff' }}>
                        <TabButton id="myGonkles" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.myGonkles} {t('myGonkles_tab')} ({myVibes.length})</TabButton>
                        <TabButton id="joined" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.joined} {t('joined_tab')} ({joinedVibes.length})</TabButton>
                        <TabButton id="pending" activeTab={activeTab} setActiveTab={setActiveTab}>{ICONS.pending} {t('pending_tab')} ({pendingVibes.length})</TabButton>
                    </div>
                </div>

                <div style={{ flex: 1, overflow: 'hidden', padding: '20px', background: '#fafbfc' }}>
                    {isLoading ? (
                        <p style={{textAlign: 'center', color: '#4a5568', fontWeight: '600'}}>{t('loading')}...</p>
                    ) : (
                        <div 
                            ref={containerRef}
                            style={containerStyle}
                            onScroll={handleScroll}
                        >
                            <div style={contentStyle}>
                                {visibleItems.map(({ item: event, index }) => {
                                    const Component = activeTab === 'myGonkles' ? MyEventsTab : 
                                                       activeTab === 'joined' ? JoinedTab : PendingTab;
                                    return (
                                        <div
                                            key={event.id}
                                            style={{
                                                position: 'absolute',
                                                top: `${index * 80}px`,
                                                left: 0,
                                                right: 0,
                                                height: '80px'
                                            }}
                                        >
                                            <Component
                                                events={[event]}
                                                onEventClick={onEventClick}
                                                handleDelete={activeTab === 'myGonkles' ? handleDeleteWrapper : undefined}
                                                handleLeave={activeTab !== 'myGonkles' ? handleLeaveWrapper : undefined}
                                                handleParticipantStatus={activeTab === 'myGonkles' ? handleParticipantStatusWrapper : undefined}
                                                onChatClick={(event) => { setSelectedChatEvent(event); setShowChat(true); }}
                                                t={t}
                                                i18n={i18n}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
        <AnimatePresence>
            {showChat && selectedChatEvent && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200 }}>
                    <EventChatModal event={selectedChatEvent} onClose={() => { setShowChat(false); setSelectedChatEvent(null); }} />
                </div>
            )}
        </AnimatePresence>
        <AnimatePresence>
            {showNotes && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1200 }}>
                    <NotesModal onClose={() => setShowNotes(false)} />
                </div>
            )}
        </AnimatePresence>
        <AnimatePresence>
            {confirmModal.isOpen && (
                <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                    onConfirm={confirmModal.onConfirm}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    type={confirmModal.type}
                    confirmText={confirmModal.type === 'danger' ? t('delete_button', 'Löschen') : t('leave_button', 'Verlassen')}
                    cancelText={t('cancel_button', 'Abbrechen')}
                />
            )}
        </AnimatePresence>
        </>
    );
};

const TabButton = ({ id, activeTab, setActiveTab, children }) => (
    <div 
        style={{ 
            flex: 1, 
            padding: '15px', 
            background: 'transparent', 
            borderBottom: activeTab === id ? '3px solid #6366f1' : '3px solid transparent', 
            color: activeTab === id ? '#1a202c' : '#4a5568', 
            fontWeight: activeTab === id ? '700' : '600', 
            transition: 'all 0.2s', 
            cursor: 'pointer',
            textAlign: 'center'
        }} 
        onClick={() => setActiveTab(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setActiveTab(id)}
    >
        {children}
    </div>
);

const MyEventsTab = ({ events, onEventClick, handleDelete, handleParticipantStatus, onChatClick, t, i18n }) => {
    const handleShare = (event) => {
        const acceptedCount = event.participants.filter(p => p.status === 'accepted').length;
        // Deep link: gonkle.app/gonkle/:id - will redirect after auth if needed
        const gonkleDeepLink = `${window.location.origin}/gonkle/${event.id}`;
        const eventText = `${event.title} - ${acceptedCount} participants going! 🏃 Join me on Gonkle`;
        
        if (navigator.share) {
            navigator.share({
                title: event.title,
                text: eventText,
                url: gonkleDeepLink
            }).catch(() => {});
        } else {
            // Fallback to clipboard - share both text and link
            const fullShareText = `${eventText}\n\n${gonkleDeepLink}`;
            navigator.clipboard.writeText(fullShareText).then(() => {
                alert(t('copiedToClipboard', 'Copied to clipboard!'));
            });
        }
    };

    return (
        <div style={{display:'flex', flexDirection: 'column', gap: '10px'}}>
            {events.length > 0 ? events.map(event => {
                const requests = event.participants.filter(p => p.status === 'pending');
                const acceptedCount = event.participants.filter(p => p.status === 'accepted').length - 1;
                return (
                    <div key={event.id} style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '12px', border: '3px solid #000'}}>
                        {/* Erste Zeile: Event-Info + Stats + Chat + Share + Delete */}
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: requests.length > 0 ? '10px' : '0'}}>
                            <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                                <div style={{fontSize: '0.75em', opacity: 0.8}}>
                                    {formatDateTime(event.event_date, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                            <div style={{display: 'flex', gap: '8px', fontSize: '0.8em', flexShrink: 0}}>
                                <span>✔️{acceptedCount}</span>
                                <span>❓{requests.length}</span>
                            </div>
                            <button onClick={() => onChatClick(event)} className="button-chat button-small">💬</button>
                            <button onClick={() => handleShare(event)} className="button-secondary button-small">📤</button>
                            <button onClick={() => handleDelete(event.id)} className="button-danger button-small">🗑️</button>
                        </div>
                        {/* Request-Liste */}
                        {requests.map(p => (
                            <div key={p.user_id} style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', marginTop: '6px'}}>
                                <img src={p.selfie_path ? `/${p.selfie_path.replace('.webp', '-thumb.webp')}` : ''} alt={p.nickname} style={{width: 30, height: 30, borderRadius: '50%', objectFit: 'cover'}}/>
                                <span style={{flex: 1, fontSize: '0.85em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.nickname}</span>
                                <button onClick={()=>handleParticipantStatus(event.id,p.user_id,'accepted')} className="button-success button-small">✓</button>
                                <button onClick={()=>handleParticipantStatus(event.id,p.user_id,'rejected')} className="button-danger button-small">✗</button>
                            </div>
                        ))}
                    </div>
                );
            }) : <p style={{textAlign:'center'}}>{t('no_created_gonkles')}</p>}
        </div>
    );
};

const JoinedTab = ({ events, onEventClick, handleLeave, onChatClick, t, i18n }) => {
    const handleShare = (event) => {
        const acceptedCount = event.participants.filter(p => p.status === 'accepted').length;
        // Deep link: gonkle.app/gonkle/:id - will redirect after auth if needed
        const gonkleDeepLink = `${window.location.origin}/gonkle/${event.id}`;
        const eventText = `${event.title} - ${acceptedCount} participants going! 🏃 Join me on Gonkle`;
        
        if (navigator.share) {
            navigator.share({
                title: event.title,
                text: eventText,
                url: gonkleDeepLink
            }).catch(() => {});
        } else {
            // Fallback to clipboard - share both text and link
            const fullShareText = `${eventText}\n\n${gonkleDeepLink}`;
            navigator.clipboard.writeText(fullShareText).then(() => {
                alert(t('copiedToClipboard', 'Copied to clipboard!'));
            });
        }
    };

    return (
        <div style={{display:'flex', flexDirection: 'column', gap: '8px'}}>
            {events.length > 0 ? events.map(event => {
                const acceptedCount = event.participants.filter(p => p.status === 'accepted').length;
                return (
                    <div key={event.id} style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '12px', border: '3px solid #000'}}>
                        <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                        <img src={event.creator_selfie ? `/${event.creator_selfie.replace('.webp', '-thumb.webp')}` : ''} alt={event.creator_nickname} style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}/>
                        <div style={{flex: 1, minWidth: 0}}>
                            <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                            <div style={{fontSize: '0.75em', opacity: 0.8}}>
                                w/ {event.creator_nickname} @ {new Date(event.event_date).toLocaleString(i18n.language, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                            </div>
                        </div>
                        <div style={{display: 'flex', gap: '8px', fontSize: '0.8em', flexShrink: 0}}>
                            <span>✔️{acceptedCount}</span>
                        </div>
                        <button onClick={() => onChatClick(event)} className="button-chat button-small">💬</button>
                        <button onClick={() => handleShare(event)} className="button-secondary button-small">📤</button>
                        <button onClick={() => handleLeave(event.id)} className="button-danger button-small" style={{whiteSpace: 'nowrap'}}>{t('leave_button')}</button>
                    </div>
                );
            }) : <p style={{textAlign:'center'}}>{t('no_joined_gonkles')}</p>}
        </div>
    );
};

const PendingTab = ({ events, onEventClick, handleLeave, t, i18n }) => (
    <div style={{display:'flex', flexDirection: 'column', gap: '8px'}}>
        {events.length > 0 ? events.map(event => {
            const acceptedCount = event.participants.filter(p => p.status === 'accepted').length;
            return (
                <div key={event.id} style={{display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '12px', border: '3px solid #000', opacity: 0.7}}>
                    <button onClick={() => onEventClick(event)} style={{fontSize: '1.5em', background: 'none', border: 'none', cursor: 'pointer', padding: '5px'}}>📍</button>
                    <img src={event.creator_selfie ? `/${event.creator_selfie.replace('.webp', '-thumb.webp')}` : ''} alt={event.creator_nickname} style={{width: 40, height: 40, borderRadius: '50%', objectFit: 'cover'}}/>
                    <div style={{flex: 1, minWidth: 0}}>
                        <div style={{fontWeight: 'bold', fontSize: '0.9em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{event.title}</div>
                        <div style={{fontSize: '0.75em', opacity: 0.8}}>
                            {t('with_creator_at_time', { creator: event.creator_nickname, time: formatDateTime(event.event_date, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) })}
                        </div>
                    </div>
                    <div style={{display: 'flex', gap: '8px', fontSize: '0.8em', flexShrink: 0}}>
                        <span>✔️{acceptedCount}</span>
                    </div>
                    <button onClick={() => handleLeave(event.id)} className="button-danger button-small" style={{whiteSpace: 'nowrap'}}>{t('cancel_button')}</button>
                </div>
            );
        }) : <p style={{textAlign:'center'}}>{t('no_pending_gonkles')}</p>}
    </div>
);

export default React.memo(MyEventsModal);
